import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import StoreCard from './StoreCard';
import { vetrineService } from '../../../services/vetrineService';

const ZoneSection = ({ filters }) => {
  const [selectedZone, setSelectedZone] = useState('centro-storico');
  const [stores, setStores] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sync selectedZone with filters.zone when filter is applied
  React.useEffect(() => {
    if (filters?.zone) {
      setSelectedZone(filters?.zone);
    }
  }, [filters?.zone]);

  // Load stores and zones on mount
  useEffect(() => {
    loadData();
  }, []);

  // Transform database store format to StoreCard expected format
  const transformStoreForCard = (store) => {
    // Parse gallery images if it's a JSON string
    let galleryImages = [];
    if (store?.galleryProdotti) {
      try {
        galleryImages = typeof store?.galleryProdotti === 'string' 
          ? JSON.parse(store?.galleryProdotti) 
          : store?.galleryProdotti;
      } catch (e) {
        console.error('Error parsing gallery images:', e);
        galleryImages = [];
      }
    }

    // Build images array from available images
    const images = [];
    if (store?.immaginePrincipale) {
      images?.push({
        url: store?.immaginePrincipale,
        alt: `${store?.nomeNegozio} - Immagine principale`
      });
    }
    if (Array.isArray(galleryImages)) {
      galleryImages?.forEach((img, index) => {
        images?.push({
          url: img,
          alt: `${store?.nomeNegozio} - Galleria immagine ${index + 1}`
        });
      });
    }

    // If no images, use placeholder
    if (images?.length === 0) {
      images?.push({
        url: '/assets/images/no_image.png',
        alt: 'Immagine non disponibile'
      });
    }

    return {
      id: store?.id,
      name: store?.nomeNegozio,
      category: store?.categoria,
      zone: store?.zona,
      rating: store?.rating || 0,
      reviewCount: store?.numeroRecensioni || 0,
      isOpen: true, // Could be calculated based on orariApertura
      distance: 0, // Could be calculated if user location available
      logo: store?.logoNegozio || '/assets/images/no_image.png',
      logoAlt: `${store?.nomeNegozio} - Logo`,
      images: images,
      badge: store?.inEvidenza ? 'In Evidenza' : (store?.tipoVetrina === 'demo' ? 'DEMO' : null),
      promotion: store?.promoTitolo || null,
      cashback: store?.cashback || null
    };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [storesData, zonesData] = await Promise.all([
        // Filter at query level: only cliccami or both platform_target
        vetrineService?.getStoresByPlatform('cliccami'),
        vetrineService?.getZones()
      ]);

      // Transform stores to match StoreCard format
      const transformedStores = storesData?.map(transformStoreForCard) || [];
      setStores(transformedStores);
      
      // Calculate zone counts dynamically from actual store data
      const zonesWithCounts = zonesData?.map(zone => {
        const count = transformedStores?.filter(store => store?.zone === zone?.value)?.length || 0;
        return {
          id: zone?.value,
          name: zone?.label,
          count: count
        };
      }) || [];

      setZones(zonesWithCounts);
      
      // Set first zone with stores as default
      const firstZoneWithStores = zonesWithCounts?.find(z => z?.count > 0);
      if (firstZoneWithStores) {
        setSelectedZone(firstZoneWithStores?.id);
      }
    } catch (error) {
      console.error('Error loading zone data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters to stores
  const applyFilters = (storesList) => {
    let filtered = [...storesList];

    // Filter by zone (selected zone)
    filtered = filtered?.filter(store => store?.zone === selectedZone);

    // Filter by category
    if (filters?.category) {
      filtered = filtered?.filter(store => store?.category === filters?.category);
    }

    // Filter by open now
    if (filters?.openNow) {
      filtered = filtered?.filter(store => store?.isOpen === true);
    }

    // Filter by has promotions
    if (filters?.hasPromo) {
      filtered = filtered?.filter(store => store?.promotion);
    }

    // Filter by has cashback
    if (filters?.hasCashback) {
      filtered = filtered?.filter(store => store?.cashback);
    }

    // Filter by featured
    if (filters?.featured) {
      filtered = filtered?.filter(store => store?.badge === 'In Evidenza');
    }

    // Filter by distance (if distance is less than max, show all; in real app would calculate actual distance)
    // For now, we'll keep all stores as we don't have user location
    // In production, you'd calculate distance based on user's geolocation

    // Sort stores
    if (filters?.sortBy) {
      switch (filters?.sortBy) {
        case 'rating':
          filtered = filtered?.sort((a, b) => (b?.rating || 0) - (a?.rating || 0));
          break;
        case 'distance':
          filtered = filtered?.sort((a, b) => (a?.distance || 0) - (b?.distance || 0));
          break;
        case 'name':
          filtered = filtered?.sort((a, b) => (a?.name || '')?.localeCompare(b?.name || ''));
          break;
        case 'newest':
          // Would need a createdAt field to sort by newest
          // For now, keep original order
          break;
        case 'relevance':
        default:
          // Keep original order for relevance
          break;
      }
    }

    return filtered;
  };

  // Filter stores by selected zone and all other filters
  const currentStores = applyFilters(stores);

  if (loading) {
    return (
      <section className="py-8 md:py-12">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-12">
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
          <Icon name="MapPin" size={24} className="text-secondary" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl text-foreground">
            Vetrine per zona
          </h2>
          <p className="font-caption text-sm text-muted-foreground">
            Esplora le attività nelle diverse zone di Macerata
          </p>
        </div>
      </div>
      <div className="mb-6 md:mb-8">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
          {zones?.map((zone) =>
          <button
            key={zone?.id}
            onClick={() => setSelectedZone(zone?.id)}
            className={`flex-shrink-0 px-4 py-2.5 md:px-5 md:py-3 rounded-lg font-caption font-medium text-sm transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 ${
            selectedZone === zone?.id ?
            'bg-primary text-primary-foreground' :
            'bg-muted text-muted-foreground hover:bg-muted/80'}`
            }>

              <span className="whitespace-nowrap">{zone?.name}</span>
              <span className="ml-2 opacity-75">({zone?.count})</span>
            </button>
          )}
        </div>
      </div>
      {currentStores?.length > 0 ?
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {currentStores?.map((store) =>
        <StoreCard key={store?.id} store={store} />
        )}
        </div> :

      <div className="text-center py-12 md:py-16">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Store" size={32} className="text-muted-foreground" />
          </div>
          <p className="font-body text-base md:text-lg text-muted-foreground">
            Nessuna vetrina disponibile in questa zona
          </p>
        </div>
      }
    </section>
  );
};

export default ZoneSection;